import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Users } from 'lucide-react';
import {
  Badge,
  Button,
  ProgressBar,
  Select,
  Textarea,
  cn,
  formatDateOnly,
  type BadgeTone,
} from '@hrms/ui';
import {
  SURVEY_MIN_ANONYMOUS_RESPONSES,
  opinionScaleLabels,
  type MySurvey,
  type PollResults,
  type SurveyAnswerInput,
  type SurveyAudience,
  type SurveyQuestion,
  type SurveyQuestionType,
  type SurveyStatus,
} from '@hrms/shared';
import { tsr } from '../../lib/api.js';

/**
 * Pieces shared by the employee survey pages, the Home poll card and the admin pages (ADR 0027).
 */

/** Every survey query sits under this key, so one invalidation refreshes every view. */
export const SURVEY_QUERY_KEY = ['surveys'] as const;

export function useInvalidateSurveys() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: SURVEY_QUERY_KEY });
}

export function surveyErrorMessage(error: unknown): string {
  if (error instanceof Error) return "Can't reach the server. Check your connection and try again.";
  const body = (error as { body?: unknown } | undefined)?.body;
  if (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string') {
    return body.message;
  }
  return 'Something went wrong. Please try again.';
}

export const QUESTION_TYPE_LABELS: Record<SurveyQuestionType, string> = {
  rating: 'Rating (1-5)',
  enps: 'eNPS (0-10)',
  opinion_scale: 'Opinion scale (1-5)',
  single_choice: 'Single choice',
  multiple_choice: 'Multiple choice',
  dropdown: 'Drop-down',
  yes_no: 'Yes / No',
  matrix: 'Matrix (grid)',
  date: 'Date',
  text: 'Free text',
  long_text: 'Long text',
};

const STATUS_TONE: Record<SurveyStatus, BadgeTone> = {
  draft: 'neutral',
  scheduled: 'info',
  open: 'success',
  closed: 'warning',
};

const STATUS_LABEL: Record<SurveyStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  open: 'Open',
  closed: 'Closed',
};

export function SurveyStatusBadge({ status }: { status: SurveyStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge>;
}

export function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

export function people(count: number): string {
  return `${count} ${count === 1 ? 'person' : 'people'}`;
}

/** One answer being filled in; only the field for the question's type is read. */
export type AnswerDraft = Omit<SurveyAnswerInput, 'questionId'>;

export function isAnswered(question: SurveyQuestion, answer: AnswerDraft | undefined): boolean {
  if (!answer) return false;
  switch (question.type) {
    case 'rating':
    case 'opinion_scale':
    case 'enps':
      return answer.number !== undefined;
    case 'text':
    case 'long_text':
      return Boolean(answer.text?.trim());
    case 'date':
      return Boolean(answer.date);
    case 'matrix':
      return (answer.matrix?.length ?? 0) > 0;
    default:
      return (answer.optionIds?.length ?? 0) > 0;
  }
}

const SCALE = { rating: [1, 2, 3, 4, 5], enps: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] } as const;
const SCALE_ENDS = {
  rating: ['Poor', 'Excellent'],
  enps: ['Not at all likely', 'Extremely likely'],
} as const;

/** Generic 1-5 satisfaction captions — the question only stores a number, so these are a fixed,
 *  reusable UI decoration (same idea as SCALE_ENDS), not per-question data from the server. */
const RATING_META: Record<number, { emoji: string; label: string; description: string }> = {
  1: { emoji: '😣', label: 'Very Dissatisfied', description: 'Needs urgent attention' },
  2: { emoji: '🙁', label: 'Somewhat Dissatisfied', description: 'Room for improvement' },
  3: { emoji: '😐', label: 'Neutral', description: 'Neither good nor bad' },
  4: { emoji: '🙂', label: 'Satisfied', description: 'Generally positive' },
  5: { emoji: '🤩', label: 'Very Satisfied', description: 'Excellent experience' },
};

/** Mirrors the 0-6 / 7-8 / 9-10 split `enpsOf` (packages/shared/src/survey.ts) scores by. */
export function npsCategory(n: number): 'detractor' | 'passive' | 'promoter' {
  if (n <= 6) return 'detractor';
  if (n <= 8) return 'passive';
  return 'promoter';
}

const NPS_CATEGORY_META = {
  detractor: {
    label: 'Detractor (0-6)',
    dot: 'bg-danger',
    message: 'Thank you for the honest feedback — we hear you.',
  },
  passive: {
    label: 'Passive (7-8)',
    dot: 'bg-warning',
    message: "Thanks for sharing — there's room to win you over.",
  },
  promoter: {
    label: 'Promoter (9-10)',
    dot: 'bg-success',
    message: 'We appreciate your loyalty and advocacy.',
  },
} as const;

/** The input for one question, by type: a scale of buttons, radio or check boxes, or free text. */
export function QuestionField({
  question,
  value,
  onChange,
  labelledBy,
}: {
  question: SurveyQuestion;
  value: AnswerDraft | undefined;
  onChange: (answer: AnswerDraft) => void;
  /** Id of the element holding the prompt, so every control is named by it. */
  labelledBy?: string;
}) {
  const name = `question-${question.id}`;
  const a11yName = labelledBy
    ? { 'aria-labelledby': labelledBy }
    : { 'aria-label': question.prompt };

  if (question.type === 'rating') {
    const scale = SCALE.rating;
    return (
      <div role="radiogroup" {...a11yName} className="grid grid-cols-2 gap-2.5 sm:grid-cols-5">
        {scale.map((n) => {
          const meta = RATING_META[n]!;
          const selected = value?.number === n;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange({ number: n })}
              className={cn(
                'relative flex cursor-pointer flex-col items-center gap-1 rounded-lg border px-3 py-4 text-center',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                selected
                  ? 'border-accent bg-accent-subtle'
                  : 'border-border bg-surface hover:border-border-strong hover:bg-bg-subtle',
              )}
            >
              {selected && (
                <CheckCircle2
                  className="absolute right-1.5 top-1.5 size-4 text-accent"
                  aria-hidden="true"
                />
              )}
              <span className="text-2xl" aria-hidden="true">
                {meta.emoji}
              </span>
              <span className="text-sm font-semibold text-fg">{meta.label}</span>
              <span className="text-xs text-fg-muted">{meta.description}</span>
            </button>
          );
        })}
      </div>
    );
  }

  if (question.type === 'enps') {
    const [low, high] = SCALE_ENDS.enps;
    const scale: readonly number[] = SCALE.enps;
    const category = value?.number !== undefined ? npsCategory(value.number) : null;
    return (
      <div className="flex max-w-2xl flex-col gap-3">
        <div role="group" {...a11yName} className="flex flex-wrap gap-1.5">
          {scale.map((n) => {
            const selected = value?.number === n;
            return (
              <button
                key={n}
                type="button"
                aria-pressed={selected}
                aria-label={`${n}${n === scale[0] ? ` (${low})` : ''}${
                  n === scale.at(-1) ? ` (${high})` : ''
                }`}
                onClick={() => onChange({ number: n })}
                className={cn(
                  'h-9 min-w-9 flex-1 cursor-pointer rounded-md border text-sm font-semibold tabular-nums',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                  selected
                    ? 'border-accent bg-accent text-fg-on-accent'
                    : 'border-border bg-surface text-fg hover:border-border-strong hover:bg-bg-subtle',
                )}
              >
                {n}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-fg-muted">
          <span>{low}</span>
          {(Object.keys(NPS_CATEGORY_META) as (keyof typeof NPS_CATEGORY_META)[]).map((key) => (
            <span key={key} className="flex items-center gap-1">
              <span className={cn('size-1.5 rounded-full', NPS_CATEGORY_META[key].dot)} />
              {NPS_CATEGORY_META[key].label}
            </span>
          ))}
          <span>{high}</span>
        </div>
        {category && value?.number !== undefined && (
          <p className="rounded-md bg-accent-subtle px-3 py-2 text-sm text-fg">
            <span className="font-semibold">
              Selected: {value.number} — {NPS_CATEGORY_META[category].label.split(' (')[0]}.
            </span>{' '}
            {NPS_CATEGORY_META[category].message}
          </p>
        )}
      </div>
    );
  }

  if (question.type === 'opinion_scale') {
    const labels = opinionScaleLabels(question.options);
    return (
      <div className="flex max-w-2xl flex-col gap-2">
        <div role="radiogroup" {...a11yName} className="grid grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5].map((n) => {
            const selected = value?.number === n;
            return (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={String(n)}
                onClick={() => onChange({ number: n })}
                className={cn(
                  'h-10 cursor-pointer rounded-md border text-sm font-semibold tabular-nums',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                  selected
                    ? 'border-accent bg-accent text-fg-on-accent'
                    : 'border-border bg-surface text-fg hover:border-border-strong hover:bg-bg-subtle',
                )}
              >
                {n}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap justify-between gap-2 text-xs text-fg-muted">
          <span>1 · {labels.low}</span>
          <span>{labels.high} · 5</span>
        </div>
      </div>
    );
  }

  if (question.type === 'date') {
    return (
      <input
        type="date"
        {...a11yName}
        value={value?.date ?? ''}
        onChange={(e) => onChange(e.target.value ? { date: e.target.value } : {})}
        className="h-(--control-height) w-full max-w-xs rounded-md border border-border bg-surface px-3 text-sm text-fg focus:outline-none focus:ring-1 focus:ring-accent"
      />
    );
  }

  if (question.type === 'text' || question.type === 'long_text') {
    const text = value?.text ?? '';
    return (
      <div className="flex flex-col gap-1">
        <Textarea
          {...a11yName}
          rows={question.type === 'long_text' ? 8 : 5}
          maxLength={5000}
          placeholder="Type your answer"
          value={text}
          onChange={(e) => onChange({ text: e.target.value })}
        />
        <p className="text-right text-xs text-fg-muted">{text.length} / 5000 characters</p>
      </div>
    );
  }

  if (question.type === 'dropdown') {
    return (
      <Select
        {...a11yName}
        className="w-full max-w-sm"
        value={value?.optionIds?.[0] ?? ''}
        onChange={(e) => onChange(e.target.value ? { optionIds: [e.target.value] } : {})}
      >
        <option value="">Choose one</option>
        {question.options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </Select>
    );
  }

  if (question.type === 'matrix') {
    const rows = question.matrix?.rows ?? [];
    const columns = question.matrix?.columns ?? [];
    const chosen = value?.matrix ?? [];
    return (
      <div role="group" {...a11yName} className="flex flex-col gap-3">
        {rows.map((row) => {
          const picked = chosen.find((c) => c.rowId === row.id)?.columnId;
          return (
            <fieldset
              key={row.id}
              className="flex flex-col gap-2 rounded-md border border-border p-3"
            >
              <legend className="px-1 text-sm font-medium text-fg">{row.label}</legend>
              <div className="flex flex-wrap gap-2">
                {columns.map((col) => (
                  <label
                    key={col.id}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm text-fg',
                      'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-accent',
                      picked === col.id
                        ? 'border-accent bg-accent-subtle'
                        : 'border-border hover:bg-bg-subtle',
                    )}
                  >
                    <input
                      type="radio"
                      name={`${name}-${row.id}`}
                      className="accent-accent"
                      checked={picked === col.id}
                      onChange={() =>
                        onChange({
                          matrix: [
                            ...chosen.filter((c) => c.rowId !== row.id),
                            { rowId: row.id, columnId: col.id },
                          ],
                        })
                      }
                    />
                    {col.label}
                  </label>
                ))}
              </div>
            </fieldset>
          );
        })}
      </div>
    );
  }

  const multiple = question.type === 'multiple_choice';
  const selected = value?.optionIds ?? [];
  return (
    <div role={multiple ? 'group' : 'radiogroup'} {...a11yName} className="flex flex-col gap-1.5">
      {multiple && <p className="text-xs text-fg-muted">Choose all that apply.</p>}
      {question.options.map((o) => {
        const checked = selected.includes(o.id);
        return (
          <label
            key={o.id}
            className={cn(
              'flex cursor-pointer items-center gap-2.5 rounded-md border px-3 py-2 text-sm text-fg',
              'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-accent',
              checked ? 'border-accent bg-accent-subtle' : 'border-border hover:bg-bg-subtle',
            )}
          >
            <input
              type={multiple ? 'checkbox' : 'radio'}
              name={name}
              className="accent-accent"
              checked={checked}
              onChange={() =>
                onChange({
                  optionIds: multiple
                    ? checked
                      ? selected.filter((id) => id !== o.id)
                      : [...selected, o.id]
                    : [o.id],
                })
              }
            />
            {o.label}
          </label>
        );
      })}
    </div>
  );
}

/** A poll's tally as bars; below the anonymity threshold, only the vote count. `myOptionId` marks
 *  the caller's own pick with a "Your Vote" pill — distinct from the leading (most-voted) option. */
export function PollTally({
  results,
  myOptionId,
}: {
  results: PollResults;
  myOptionId?: string;
}) {
  if (!results.distribution) {
    return (
      <p className="rounded-md bg-bg-subtle px-3 py-2 text-sm text-fg-muted">
        Thanks for voting. Results appear once {SURVEY_MIN_ANONYMOUS_RESPONSES} people have voted (
        {results.total} so far).
      </p>
    );
  }
  const top = Math.max(0, ...results.distribution.map((d) => d.count));
  return (
    <div className="flex flex-col gap-2.5">
      {results.distribution.map((d) => {
        const pct = results.total === 0 ? 0 : Math.round((d.count / results.total) * 100);
        const leading = top > 0 && d.count === top;
        const mine = myOptionId === d.value;
        return (
          <div
            key={d.value}
            className={cn(
              'flex flex-col gap-1 rounded-lg p-2',
              mine && 'bg-accent-subtle ring-1 ring-accent',
            )}
          >
            <p className="flex flex-wrap items-center justify-between gap-2 text-sm text-fg">
              <span className="flex min-w-0 items-center gap-1.5">
                {mine && (
                  <Badge tone="info" className="shrink-0">
                    <CheckCircle2 className="mr-1 inline size-3" aria-hidden="true" />
                    Your Vote
                  </Badge>
                )}
                <span className={cn('min-w-0 break-words', leading && 'font-semibold')}>
                  {d.label}
                </span>
              </span>
              <span className="shrink-0 tabular-nums text-fg-muted">
                {d.count} {plural(d.count, 'vote')} · {pct}%
              </span>
            </p>
            <ProgressBar
              value={pct}
              ariaLabel={`${d.label}: ${pct}%`}
              color={mine ? 'var(--accent)' : leading ? 'var(--accent)' : 'var(--fg-muted)'}
            />
          </div>
        );
      })}
      <p className="text-xs text-fg-muted">{plural(results.total, 'vote')} tallied</p>
    </div>
  );
}

/** A poll answered inline: the question and options, then the tally once voted. */
export function PollVote({ poll }: { poll: MySurvey }) {
  const invalidate = useInvalidateSurveys();
  const vote = tsr.surveys.submitMyResponse.useMutation();
  const [answer, setAnswer] = useState<AnswerDraft | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<PollResults | null>(poll.pollResults);
  const [myOptionId, setMyOptionId] = useState<string | undefined>();
  const question = poll.pollQuestion;
  if (!question) return null;
  const promptId = `poll-${poll.id}-prompt`;

  function submit() {
    if (!question) return;
    setError(null);
    vote.mutate(
      {
        params: { surveyId: poll.id },
        body: { answers: [{ questionId: question.id, ...answer }] },
      },
      {
        onSuccess: (res) => {
          if (res.status === 201) setResults(res.body.pollResults);
          setMyOptionId(answer?.optionIds?.[0]);
          invalidate();
        },
        onError: (err) => setError(surveyErrorMessage(err)),
      },
    );
  }

  const voted = Boolean(poll.completedAt) || results !== null;
  return (
    <div className="flex flex-col gap-3">
      <p id={promptId} className="text-base font-semibold text-fg">
        {question.prompt}
      </p>
      {voted ? (
        results ? (
          <PollTally results={results} myOptionId={myOptionId} />
        ) : (
          <p className="text-sm text-fg-muted">Thanks for voting.</p>
        )
      ) : (
        <>
          <p className="-mt-1.5 text-xs text-fg-muted">
            Select one option to cast your vote.
            {poll.anonymous ? ' Submissions are anonymous to peers.' : ''}
          </p>
          <QuestionField
            question={question}
            value={answer}
            onChange={setAnswer}
            labelledBy={promptId}
          />
          {error && (
            <p role="alert" className="text-xs text-danger">
              {error}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-fg-muted">
              {poll.voteCount
                ? `${plural(poll.voteCount, 'person')} already voted · `
                : ''}
              Results reveal after you vote.
            </p>
            <Button
              variant="primary"
              size="sm"
              loading={vote.isPending}
              disabled={!isAnswered(question, answer)}
              onClick={submit}
            >
              Submit Vote
            </Button>
          </div>
        </>
      )}
      <p className="text-xs text-fg-muted">
        {poll.anonymous ? 'Anonymous: your vote is not linked to you.' : 'Your name is recorded.'}
        {poll.closesOn ? ` Closes ${formatDateOnly(poll.closesOn)}.` : ''}
      </p>
    </div>
  );
}

/** A live "how many people" count for the audience being built, debounced so it is not one
 *  request per checkbox click. Admin-only data (`survey:manage`), used while composing — never
 *  shown to an employee. A POST route (the audience is a body, not a resource id), so this drives
 *  it through `.useMutation()` on a timer rather than `.useQuery()` — same pattern as the
 *  Announcements composer's `ReachEstimate`. */
export function SurveyReachEstimate({ audience }: { audience: SurveyAudience }) {
  const [count, setCount] = useState<number | null>(null);
  const estimate = tsr.surveys.estimateSurveyReach.useMutation();
  const estimateRef = useRef(estimate);
  estimateRef.current = estimate;

  useEffect(() => {
    const id = setTimeout(() => {
      estimateRef.current.mutate(
        { body: { audience } },
        { onSuccess: (res) => res.status === 200 && setCount(res.body.count) },
      );
    }, 400);
    return () => clearTimeout(id);
  }, [audience]);

  return (
    <p className="flex items-center gap-1.5 text-xs text-fg-muted">
      <Users className="size-3.5" aria-hidden="true" />
      {count === null ? 'Estimating reach…' : `Reach: ~${count} ${count === 1 ? 'person' : 'people'}`}
    </p>
  );
}
